import React, { useState } from "react";
import { Card, Alert, Spinner, Table, Button } from "react-bootstrap";
import ReactMarkdown from "react-markdown";

const AiOverviewResultDisplay = ({
  isLoading,
  result,
  error,
  title = "AI Overview Results",
  inputParameters = null,
  onCopy,
}) => {
  const [copiedIndex, setCopiedIndex] = useState(null);

  const handleCopy = async (content, customMessage, index = null) => {
    try {
      await navigator.clipboard.writeText(content);
      if (index !== null) {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
      }
      if (onCopy) {
        onCopy(customMessage || "Content copied to clipboard!");
      }
    } catch (err) {
      console.error("Failed to copy content: ", err);
      if (onCopy) {
        onCopy("Failed to copy content", "error");
      }
    }
  };

  const renderInputParameters = () => {
    if (!inputParameters) return null;

    return (
      <div className="px-3 py-2 bg-light border-bottom">
        <small className="text-muted fw-bold d-block mb-1">
          Generated with:
        </small>
        <div className="d-flex flex-wrap gap-2">
          {Object.entries(inputParameters).map(([key, value]) => {
            if (!value) return null;

            let displayKey = key;
            let displayValue = value;

            // Format key names
            switch (key) {
              case "keyword":
              case "primary_keyword":
                displayKey = "Keyword";
                break;
              case "brand":
                displayKey = "Brand";
                break;
              case "noQueries":
              case "no_queries":
                displayKey = "Queries";
                break;
              default:
                displayKey = key.charAt(0).toUpperCase() + key.slice(1);
            }

            return (
              <span key={key} className="badge bg-secondary text-white">
                <strong>{displayKey}:</strong> {displayValue}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const renderContent = (content) => {
    if (typeof content === "string") {
      // Check if it's markdown content
      if (content.includes("#") || content.includes("**") || content.includes("*")) {
        return (
          <ReactMarkdown
            components={{
              h1: ({ children }) => (
                <h4 className="text-primary fw-bold mb-3 mt-2" style={{ fontSize: "18px" }}>
                  {children}
                </h4>
              ),
              h2: ({ children }) => (
                <h5 className="text-primary fw-bold mb-3 mt-4" style={{ fontSize: "16px" }}>
                  {children}
                </h5>
              ),
              h3: ({ children }) => (
                <h6 className="text-dark fw-bold mb-2 mt-3" style={{ fontSize: "15px" }}>
                  {children}
                </h6>
              ),
              ul: ({ children }) => (
                <ul className="mb-3" style={{ paddingLeft: "20px" }}>
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="mb-3" style={{ paddingLeft: "20px" }}>
                  {children}
                </ol>
              ),
              li: ({ children }) => (
                <li className="mb-1" style={{ fontSize: "13px" }}>
                  {children}
                </li>
              ),
              strong: ({ children }) => (
                <strong className="text-dark fw-bold">{children}</strong>
              ),
              p: ({ children }) => (
                <p className="mb-2" style={{ fontSize: "13px" }}>
                  {children}
                </p>
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        );
      } else {
        return (
          <div style={{ fontSize: "14px", lineHeight: "1.5", whiteSpace: "pre-wrap" }}>
            {content}
          </div>
        );
      }
    } else {
      return (
        <pre style={{ fontSize: "12px", whiteSpace: "pre-wrap" }}>
          {JSON.stringify(content, null, 2)}
        </pre>
      );
    }
  };

  if (!isLoading && !result && !error) {
    return null;
  }

  return (
    <Card className="mt-3 border-0 shadow-sm">
      <Card.Header className="bg-light border-0">
        <h6 className="mb-0 fw-bold text-primary">{title}</h6>
      </Card.Header>

      {/* Input Parameters Summary */}
      {renderInputParameters()}

      <Card.Body className="p-0">
        {isLoading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="me-2" />
            <span className="text-muted">Generating AI overview...</span>
          </div>
        )}

        {error && (
          <div className="p-3">
            <Alert variant="danger" className="mb-0">
              <small>{error}</small>
            </Alert>
          </div>
        )}

        {result && !isLoading && (
          <div className="p-3">
            {Array.isArray(result) ? (
              // Handle array response (list of overviews)
              <div className="table-responsive">
                <Table striped hover>
                  <thead className="table-dark">
                    <tr>
                      <th scope="col" style={{ width: "60px", color: "black" }}>
                        #
                      </th>
                      <th scope="col" style={{ color: "black" }}>AI Overview</th>
                      <th scope="col" style={{ width: "100px", color: "black" }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>
                          <div
                            style={{
                              maxHeight: "300px",
                              overflowY: "auto",
                              fontSize: "14px",
                              lineHeight: "1.5",
                            }}
                          >
                            {typeof item === "object" && item !== null && !Array.isArray(item) ? (
                              <div>
                                {Object.entries(item).map(([question, answer], entryIndex) => (
                                  <div key={entryIndex}>
                                    <div className="text-primary fw-bold mb-2" style={{ fontSize: "14px" }}>
                                      {question}
                                    </div>
                                    <div className="text-success" style={{ fontSize: "14px" }}>
                                      <ReactMarkdown
                                        components={{
                                          p: ({ children }) => (
                                            <p className="mb-1">{children}</p>
                                          ),
                                          strong: ({ children }) => (
                                            <strong className="fw-bold">{children}</strong>
                                          ),
                                          em: ({ children }) => <em>{children}</em>,
                                          ul: ({ children }) => (
                                            <ul
                                              className="mb-1"
                                              style={{ paddingLeft: "15px" }}
                                            >
                                              {children}
                                            </ul>
                                          ),
                                          ol: ({ children }) => (
                                            <ol
                                              className="mb-1"
                                              style={{ paddingLeft: "15px" }}
                                            >
                                              {children}
                                            </ol>
                                          ),
                                          li: ({ children }) => (
                                            <li className="mb-0">{children}</li>
                                          ),
                                          h1: ({ children }) => (
                                            <h6 className="fw-bold mb-1">
                                              {children}
                                            </h6>
                                          ),
                                          h2: ({ children }) => (
                                            <h6 className="fw-bold mb-1">
                                              {children}
                                            </h6>
                                          ),
                                          h3: ({ children }) => (
                                            <h6 className="fw-bold mb-1">{children}</h6>
                                          ),
                                          code: ({ children }) => (
                                            <code className="bg-light px-1 rounded small">
                                              {children}
                                            </code>
                                          ),
                                        }}
                                      >
                                        {answer}
                                      </ReactMarkdown>
                                    </div>
                                    {entryIndex < Object.entries(item).length - 1 && (
                                      <hr className="my-2" style={{ opacity: 0.3 }} />
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : typeof item === "string" ? (
                              <ReactMarkdown
                                components={{
                                  p: ({ children }) => (
                                    <p className="mb-1">{children}</p>
                                  ),
                                  strong: ({ children }) => (
                                    <strong className="fw-bold">{children}</strong>
                                  ),
                                  em: ({ children }) => <em>{children}</em>,
                                  ul: ({ children }) => (
                                    <ul
                                      className="mb-1"
                                      style={{ paddingLeft: "15px" }}
                                    >
                                      {children}
                                    </ul>
                                  ),
                                  ol: ({ children }) => (
                                    <ol
                                      className="mb-1"
                                      style={{ paddingLeft: "15px" }}
                                    >
                                      {children}
                                    </ol>
                                  ),
                                  li: ({ children }) => (
                                    <li className="mb-0">{children}</li>
                                  ),
                                  h1: ({ children }) => (
                                    <h6 className="fw-bold text-primary mb-1">
                                      {children}
                                    </h6>
                                  ),
                                  h2: ({ children }) => (
                                    <h6 className="fw-bold text-primary mb-1">
                                      {children}
                                    </h6>
                                  ),
                                  h3: ({ children }) => (
                                    <h6 className="fw-bold mb-1">{children}</h6>
                                  ),
                                  code: ({ children }) => (
                                    <code className="bg-light px-1 rounded small">
                                      {children}
                                    </code>
                                  ),
                                }}
                              >
                                {item}
                              </ReactMarkdown>
                            ) : (
                              <pre className="mb-0 small text-wrap">
                                {JSON.stringify(item, null, 2)}
                              </pre>
                            )}
                          </div>
                        </td>
                        <td>
                          <Button
                            variant={
                              copiedIndex === index
                                ? "success"
                                : "outline-primary"
                            }
                            size="sm"
                            onClick={() => handleCopy(
                              typeof item === "object" && item !== null && !Array.isArray(item)
                                ? Object.entries(item).map(([question, answer]) => `Q: ${question}\n\nA: ${answer}`).join('\n\n---\n\n')
                                : typeof item === "string" ? item : JSON.stringify(item, null, 2),
                              "AI Overview copied!",
                              index
                            )}
                            disabled={copiedIndex === index}
                            className="px-2"
                          >
                            <i
                              className={`fas ${
                                copiedIndex === index ? "fa-check" : "fa-copy"
                              } me-1`}
                            ></i>
                            {copiedIndex === index ? "Copied!" : "Copy"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : typeof result === "object" && result !== null ? (
              // Handle object response (question-answer pairs)
              <div className="table-responsive">
                <Table striped hover>
                  <thead className="table-dark">
                    <tr>
                      <th scope="col" style={{ width: "60px", color: "black" }}>
                        #
                      </th>
                      <th scope="col" style={{ color: "black" }}>AI Overview</th>
                      <th scope="col" style={{ width: "100px", color: "black" }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(result).map(([question, answer], index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>
                          <div
                            style={{
                              maxHeight: "300px",
                              overflowY: "auto",
                              fontSize: "14px",
                              lineHeight: "1.5",
                            }}
                          >
                            <div className="text-primary fw-bold mb-2" style={{ fontSize: "14px" }}>
                              {question}
                            </div>
                            <div className="text-success" style={{ fontSize: "14px" }}>
                              <ReactMarkdown
                                components={{
                                  p: ({ children }) => (
                                    <p className="mb-1">{children}</p>
                                  ),
                                  strong: ({ children }) => (
                                    <strong className="fw-bold">{children}</strong>
                                  ),
                                  em: ({ children }) => <em>{children}</em>,
                                  ul: ({ children }) => (
                                    <ul
                                      className="mb-1"
                                      style={{ paddingLeft: "15px" }}
                                    >
                                      {children}
                                    </ul>
                                  ),
                                  ol: ({ children }) => (
                                    <ol
                                      className="mb-1"
                                      style={{ paddingLeft: "15px" }}
                                    >
                                      {children}
                                    </ol>
                                  ),
                                  li: ({ children }) => (
                                    <li className="mb-0">{children}</li>
                                  ),
                                  h1: ({ children }) => (
                                    <h6 className="fw-bold mb-1">
                                      {children}
                                    </h6>
                                  ),
                                  h2: ({ children }) => (
                                    <h6 className="fw-bold mb-1">
                                      {children}
                                    </h6>
                                  ),
                                  h3: ({ children }) => (
                                    <h6 className="fw-bold mb-1">{children}</h6>
                                  ),
                                  code: ({ children }) => (
                                    <code className="bg-light px-1 rounded small">
                                      {children}
                                    </code>
                                  ),
                                }}
                              >
                                {answer}
                              </ReactMarkdown>
                            </div>
                          </div>
                        </td>
                        <td>
                          <Button
                            variant={
                              copiedIndex === index
                                ? "success"
                                : "outline-primary"
                            }
                            size="sm"
                            onClick={() => handleCopy(
                              `Q: ${question}\n\nA: ${typeof answer === "string" ? answer : JSON.stringify(answer, null, 2)}`,
                              "Q&A copied!",
                              index
                            )}
                            disabled={copiedIndex === index}
                            className="px-2"
                          >
                            <i
                              className={`fas ${
                                copiedIndex === index ? "fa-check" : "fa-copy"
                              } me-1`}
                            ></i>
                            {copiedIndex === index ? "Copied!" : "Copy"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              // Handle single string response
              <div className="position-relative">
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="position-absolute"
                  style={{ top: "10px", right: "10px", zIndex: 5 }}
                  onClick={() => handleCopy(
                    typeof result === "string" ? result : JSON.stringify(result, null, 2),
                    "AI Overview copied!"
                  )}
                >
                  Copy
                </Button>
                <div className="pe-5">
                  {renderContent(result)}
                </div>
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default AiOverviewResultDisplay;
